<template>
  <!--
    FINAL CHAT V2 -- one figure the tutor drew in its reply (upstream 0a347ba,
    design A15). The spec comes from the reply's ```figure block; the drawing
    is computed here (figureLayout.js) and drawn as plain SVG in V2's palette,
    framed like V2's in-message media. Every value that reaches the page is
    bound as text or an attribute -- never as HTML. A spec that cannot be
    drawn shows its alt text instead, as upstream requires ("for clients that
    cannot render it").
  -->
  <figure
    class="ai-tutor-figure"
    :class="{ 'ai-tutor-figure--failed': !model }"
    data-testid="ai-tutor-figure"
    :data-kind="spec && spec.kind"
  >
    <svg
      v-if="model && model.type === 'svg'"
      class="ai-tutor-figure__svg"
      :viewBox="`0 0 ${model.width} ${model.height}`"
      :style="{ maxWidth: `${model.width}px` }"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      :aria-label="alt"
      direction="ltr"
    >
      <defs v-if="model.clip">
        <clipPath :id="clipId">
          <rect
            :x="model.clip.x"
            :y="model.clip.y"
            :width="model.clip.width"
            :height="model.clip.height"
          />
        </clipPath>
      </defs>
      <template v-for="(item, index) in model.items">
        <line
          v-if="item.t === 'line'"
          :key="index"
          :x1="item.x1"
          :y1="item.y1"
          :x2="item.x2"
          :y2="item.y2"
          :stroke="item.stroke"
          :stroke-width="item.width"
          :stroke-opacity="item.opacity"
          stroke-linecap="round"
        />
        <path
          v-else-if="item.t === 'path'"
          :key="index"
          :d="item.d"
          :fill="item.fill || 'none'"
          :fill-opacity="item.fillOpacity"
          :stroke="item.stroke || 'none'"
          :stroke-width="item.width"
          :clip-path="item.clip && model.clip ? `url(#${clipId})` : null"
          stroke-linejoin="round"
          stroke-linecap="round"
        />
        <circle
          v-else-if="item.t === 'circle'"
          :key="index"
          :cx="item.cx"
          :cy="item.cy"
          :r="item.r"
          :fill="item.fill"
          :fill-opacity="item.fillOpacity"
          :stroke="item.stroke"
          :stroke-width="item.width"
        />
        <polygon
          v-else-if="item.t === 'polygon'"
          :key="index"
          :points="item.points"
          :fill="item.fill"
          :fill-opacity="item.fillOpacity"
          :stroke="item.stroke"
          :stroke-width="item.width"
          stroke-linejoin="round"
        />
        <rect
          v-else-if="item.t === 'rect'"
          :key="index"
          :x="item.x"
          :y="item.y"
          :width="item.w"
          :height="item.h"
          :rx="item.rx"
          :fill="item.fill"
        />
        <text
          v-else-if="item.t === 'text'"
          :key="index"
          :x="item.x"
          :y="item.y"
          :text-anchor="item.anchor"
          :dominant-baseline="item.baseline === 'middle' ? 'central' : null"
          :font-size="item.size"
          :font-weight="item.weight"
          :fill="item.fill"
          paint-order="stroke"
          stroke="#fff"
          stroke-width="3"
          stroke-linejoin="round"
          v-text="item.text"
        />
      </template>
    </svg>

    <div
      v-else-if="model && model.type === 'sign_table'"
      class="ai-tutor-figure__table"
      role="img"
      :aria-label="alt"
    >
      <table dir="ltr">
        <thead>
          <tr>
            <th>x</th>
            <template v-for="(cell, index) in columns">
              <th
                :key="index"
                :class="{ 'ai-tutor-figure__cut': cell.type === 'cut' }"
              >
                {{ cell.head }}
              </th>
            </template>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, rowIndex) in model.rows" :key="rowIndex">
            <td class="ai-tutor-figure__factor">{{ row.label }}</td>
            <template v-for="(cell, index) in columns">
              <td
                :key="index"
                :class="{ 'ai-tutor-figure__cut': cell.type === 'cut' }"
              >
                {{
                  cell.type === 'cut'
                    ? row.zeros[cell.index]
                      ? zero
                      : ''
                    : signOf(row.signs[cell.index])
                }}
              </td>
            </template>
          </tr>
          <tr class="ai-tutor-figure__product">
            <td>{{ productLabel }}</td>
            <template v-for="(cell, index) in columns">
              <td
                :key="index"
                :class="{ 'ai-tutor-figure__cut': cell.type === 'cut' }"
              >
                {{
                  cell.type === 'cut' ? zero : signOf(model.product[cell.index])
                }}
              </td>
            </template>
          </tr>
        </tbody>
      </table>
    </div>

    <template v-else-if="model && model.type === 'image'">
      <img
        v-if="model.url && !imageFailed"
        class="ai-tutor-figure__image"
        :src="model.url"
        :alt="alt"
        loading="lazy"
        @error="imageFailed = true"
      />
      <p v-else class="ai-tutor-figure__note">
        {{ model.caption || $t('aiTutorSetup.chat.figure.bookFigure') }}
      </p>
    </template>

    <p v-if="!model" class="ai-tutor-figure__note" role="note">
      {{ $t('aiTutorSetup.chat.figure.failed') }}
    </p>
    <p v-if="model && model.footer" class="ai-tutor-figure__footer">
      {{ model.footer }}
    </p>
    <figcaption v-if="caption">{{ caption }}</figcaption>
  </figure>
</template>

<script>
import { layoutFigure } from './figureLayout'

let nextId = 0

export default {
  name: 'TutorFigure',
  props: {
    // One figure spec ({kind, alt, ...}) -- from the reply's ```figure block,
    // or the stored spec that carries a book figure's signed image URL.
    spec: {
      type: Object,
      required: true,
    },
  },
  data() {
    nextId += 1
    return {
      clipId: `ai-tutor-figure-clip-${nextId}`,
      imageFailed: false,
      // The px width the drawing gets on the page (0 until measured: the
      // layout's own 560 then), so labels keep their size on a phone.
      width: 0,
    }
  },
  computed: {
    persian() {
      return !(this.$i18n && this.$i18n.locale === 'en')
    },
    model() {
      try {
        return layoutFigure(this.spec, {
          persian: this.persian,
          width: this.width,
          labels: {
            treeStart: this.$t('aiTutorSetup.chat.figure.treeStart'),
            treeCases: this.$t('aiTutorSetup.chat.figure.treeCases'),
          },
        })
      } catch (err) {
        return null
      }
    },
    alt() {
      return typeof this.spec.alt === 'string' ? this.spec.alt : ''
    },
    // A book figure's own caption is shown in place of a missing image; the
    // alt text is the caption of everything else.
    caption() {
      if (this.model && this.model.type === 'image' && this.model.caption) {
        return this.model.url && !this.imageFailed ? this.model.caption : ''
      }
      return this.alt
    },
    // Interval / cut columns of a sign table, left to right:
    // (-inf .. c1) c1 (c1 .. c2) c2 ... (ck .. +inf)
    columns() {
      if (!this.model || this.model.type !== 'sign_table') return []
      const cuts = this.model.cuts
      const out = []
      for (let i = 0; i <= cuts.length; i += 1) {
        let head = ''
        if (i === 0) head = '−∞'
        if (i === cuts.length) head = cuts.length ? '+∞' : '−∞ … +∞'
        out.push({ type: 'interval', index: i, head })
        if (i < cuts.length) out.push({ type: 'cut', index: i, head: cuts[i] })
      }
      return out
    },
    zero() {
      return this.persian ? '۰' : '0'
    },
    productLabel() {
      return this.model && this.model.single
        ? this.$t('aiTutorSetup.chat.figure.sign')
        : this.$t('aiTutorSetup.chat.figure.product')
    },
  },
  watch: {
    'spec.imageUrl'() {
      this.imageFailed = false
    },
  },
  mounted() {
    this.measure()
    window.addEventListener('resize', this.measure)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.measure)
  },
  methods: {
    // Inner width of the frame (10px padding + 1px border each side),
    // stepped by 10px so a small resize does not re-lay the drawing out.
    measure() {
      const el = this.$el
      const inner = el && el.clientWidth ? el.clientWidth - 22 : 0
      const next = inner > 0 ? Math.floor(inner / 10) * 10 : 0
      if (next !== this.width) this.width = next
    },
    signOf(sign) {
      if (sign > 0) return '+'
      if (sign < 0) return '−'
      return this.zero
    },
  },
}
</script>

<style lang="scss" scoped>
// Framed like V2's in-message media (`.message-media`): a white inset card
// with the V2 border and radius, the caption in V2's muted small type. The
// drawing itself is always left-to-right; the caption follows the page.
.ai-tutor-figure {
  box-sizing: border-box;
  max-width: 100%;
  margin: 10px 0;
  padding: 10px;
  overflow: hidden;
  border: 1px solid $borna-border;
  border-radius: 14px;
  background: #fff;

  figcaption {
    margin-top: 6px;
    color: $borna-muted;
    font-size: 10px;
    line-height: 1.7;
  }

  &--failed {
    border-style: dashed;
  }
}

.ai-tutor-figure__svg {
  display: block;
  width: 100%;
  height: auto;
  margin: 0 auto;
  font-family: inherit;
}

.ai-tutor-figure__table {
  max-width: 100%;
  overflow-x: auto;

  table {
    margin: 2px auto;
    border-collapse: collapse;
    color: $borna-text;
    font-size: 12px;
  }

  th,
  td {
    min-width: 30px;
    padding: 5px 9px;
    border: 1px solid $borna-border;
    text-align: center;
    white-space: nowrap;
  }

  th {
    background: $borna-canvas;
    color: $borna-muted;
    font-weight: 700;
  }

  .ai-tutor-figure__cut {
    min-width: 18px;
    padding-inline: 4px;
    color: $borna-muted;
    font-size: 11px;
  }

  .ai-tutor-figure__factor {
    font-family: ui-monospace, Menlo, Consolas, monospace;
    font-size: 11px;
  }

  .ai-tutor-figure__product td {
    background: $borna-indigo-50;
    color: $borna-indigo-900;
    font-weight: 800;
  }
}

.ai-tutor-figure__image {
  display: block;
  max-width: 100%;
  max-height: 420px;
  margin: 0 auto;
  border-radius: 10px;
  object-fit: contain;
}

.ai-tutor-figure__note,
.ai-tutor-figure__footer {
  margin: 4px 0 0;
  color: $borna-muted;
  font-size: 10px;
}

.ai-tutor-figure__footer {
  color: $borna-text;
  font-weight: 700;
  text-align: center;
}
</style>
